import { Alert, AlertTitle, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, TextField, Tooltip, Typography } from '@mui/material'
import { AttachementsUpload } from '../../../shared_elements/components'
import { formatBytes } from '../../../utils/fileUpload'

// Approve / Reject dialog for the Titan Multi Approval Flow - remark + attachments, same pattern
// as the single-flow's InvoiceApprovalFlow action dialog. When the active stage has no working
// group assigned yet, a mandatory working group selection is folded into this same dialog.
const ApprovalActionDialog = ({ open, stageName, remark, remarkError, actionLoading, remainingStorage, disableReject, needsWorkingGroup, leaseSlug, workingGroups, groupsLoading, selectedGroup, groupError, onChangeGroup, onFocusGroup, onChangeRemark, onFocusRemark, onFileChange, onApprove, onReject, onCancel }) => {
    if (!open) return null
    const noWorkingGroups = needsWorkingGroup && !groupsLoading && workingGroups.length === 0
    // Only Approve advances to the next stage, so only Approve needs the working group resolved first.
    const approveBlocked = needsWorkingGroup && (groupsLoading || noWorkingGroups)
    return (
        <Dialog open maxWidth='sm' fullWidth>
            <DialogTitle>Approval Action ({stageName})</DialogTitle>
            <DialogContent dividers>
                <TextField
                    label='Reason'
                    fullWidth
                    multiline
                    rows={3}
                    margin='normal'
                    variant='outlined'
                    InputLabelProps={{ shrink: true }}
                    value={remark}
                    onChange={(e) => onChangeRemark(e.target.value)}
                    onFocus={onFocusRemark}
                    error={!!remarkError}
                    helperText={remarkError}
                    disabled={!!actionLoading}
                />
                {needsWorkingGroup && (
                    <Box sx={{ mt: 2 }}>
                        {groupsLoading ? (
                            <Box sx={{ mt: 2 }}>
                                <Skeleton variant='text' width={120} height={20} />
                                <Skeleton variant='rounded' height={40} sx={{ mt: 0.5 }} />
                            </Box>
                        ) : noWorkingGroups ? (
                            <Alert severity='warning'>
                                <AlertTitle>No Working Group Found</AlertTitle>
                                This lease doesn't have a working group set up yet. Add one before approving or rejecting this invoice.
                                <Typography
                                    component='span'
                                    variant='inherit'
                                    sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => window.open(`/lease/${leaseSlug}/lease-details/working-groups`, '_blank')}
                                >
                                    Basic Details {'>'} Working Group
                                </Typography>
                            </Alert>
                        ) : (
                            <Autocomplete
                                disableClearable
                                options={workingGroups}
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                value={selectedGroup}
                                onChange={(_, val) => onChangeGroup(val)}
                                disabled={!!actionLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        label='Next Approver Group'
                                        margin='normal'
                                        size='small'
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        error={!!groupError}
                                        helperText={groupError}
                                        disabled={!!actionLoading}
                                        onFocus={onFocusGroup}
                                    />
                                )}
                            />
                        )}
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Button disableRipple color='primary' variant='outlined' size='small' sx={{ marginBottom: '10px' }}>Attachments</Button>
                    <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary'>{`Available Space : ${formatBytes(remainingStorage)}`}</Button>
                </Box>
                <AttachementsUpload
                    onFileChange={onFileChange}
                    attachments={[]}
                    remainingStorage={remainingStorage || 0}
                    isDisabled={!!actionLoading}
                    maxFileSize={100 * 1024 * 1024}
                    maxFiles={5}
                />
            </DialogContent>
            <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginLeft: '15px' }}>
                    <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        disabled={!!actionLoading || approveBlocked}
                        onClick={onApprove}
                    >
                        {actionLoading === 'approve' ? <CircularProgress size={18} /> : 'Approve'}
                    </Button>
                    <Tooltip title={disableReject ? "The first stage can't be rejected" : ''} arrow>
                        <span>
                            <Button
                                size='small'
                                variant='contained'
                                color='error'
                                disabled={!!actionLoading || disableReject}
                                onClick={onReject}
                                style={{ marginLeft: 8 }}
                            >
                                {actionLoading === 'reject' ? <CircularProgress size={18} /> : 'Reject'}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
                <Button size='small' sx={{ marginRight: '15px' }} variant='outlined' onClick={onCancel} disabled={!!actionLoading}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ApprovalActionDialog
